import { gridRowVisibilitySelectionStateSelector } from "@components/DataGrid/hooks/features/rowVisibilitySelection/gridRowVisibilitySelector";
import { isMultipleRowSelectionEnabled } from "@components/DataGrid/hooks/features/rowVisibilitySelection/utils";
import { useGridRootProps } from "@components/DataGrid/hooks/useGridRootProps";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Checkbox, TouchRippleActions, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { unstable_composeClasses as composeClasses } from "@mui/utils";
import {
  getDataGridUtilityClass,
  GridColumnHeaderParams,
  gridExpandedSortedRowIdsSelector,
  GridHeaderSelectionCheckboxParams,
  gridPaginatedVisibleSortedGridRowIdsSelector,
  GridRowId,
  gridTabIndexColumnHeaderSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { DataGridPremiumProcessedProps } from "@mui/x-data-grid-premium/models/dataGridPremiumProps";
import PropTypes from "prop-types";
import { ChangeEvent, forwardRef, KeyboardEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

type OwnerState = { classes: DataGridPremiumProcessedProps["classes"] };

const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ["checkboxInput"],
  };

  return composeClasses(slots, getDataGridUtilityClass, classes);
};

const GridHeaderVisibilityCheckbox = forwardRef<HTMLButtonElement, GridColumnHeaderParams>((props, ref) => {
  const { field, colDef, ...other } = props;
  const [, forceUpdate] = useState(false);
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const ownerState = { classes: rootProps.classes };
  const classes = useUtilityClasses(ownerState);
  const tabIndexState = useGridSelector(apiRef, gridTabIndexColumnHeaderSelector);
  const selection = useGridSelector(apiRef, gridRowVisibilitySelectionStateSelector) || []; // TODO: state.rowVisibilitySelection is not defined yet
  const visibleRowIds = useGridSelector(apiRef, gridExpandedSortedRowIdsSelector);
  const paginatedVisibleRowIds = useGridSelector(apiRef, gridPaginatedVisibleSortedGridRowIdsSelector);

  const rippleRef = useRef<TouchRippleActions>(null);

  const filteredSelection = useMemo(() => {
    if (typeof rootProps.isRowSelectable !== "function") {
      return selection;
    }

    return selection.filter((id) => {
      // The row might have been deleted
      if (!apiRef.current.getRow(id)) {
        return false;
      }

      return rootProps.isRowSelectable!(apiRef.current.getRowParams(id));
    });
  }, [apiRef, rootProps.isRowSelectable, selection]);

  // All the rows that could be selected / unselected by toggling this checkbox
  const selectionCandidates = useMemo(() => {
    const rowIds = !rootProps.pagination || !rootProps.checkboxSelectionVisibleOnly ? visibleRowIds : paginatedVisibleRowIds;

    // Convert to an object to make O(1) checking if a row exists or not
    // TODO create selector that returns visibleRowIds/paginatedVisibleRowIds as an object
    return rowIds.reduce<Record<GridRowId, true>>((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
  }, [rootProps.pagination, rootProps.checkboxSelectionVisibleOnly, paginatedVisibleRowIds, visibleRowIds]);

  // Amount of rows selected and that are visible in the current page
  const currentSelectionSize = useMemo(() => filteredSelection.filter((id) => selectionCandidates[id]).length, [filteredSelection, selectionCandidates]);

  const isIndeterminate = currentSelectionSize > 0 && currentSelectionSize < Object.keys(selectionCandidates).length;

  const isChecked = currentSelectionSize > 0;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const params: GridHeaderSelectionCheckboxParams = {
      value: event.target.checked,
    };

    apiRef.current.publishEvent("headerVisibilitySelectionCheckboxChange", params);
  };

  const tabIndex = tabIndexState !== null && tabIndexState.field === props.field ? 0 : -1;
  useLayoutEffect(() => {
    const element = apiRef.current.getColumnHeaderElement(props.field);
    if (tabIndex === 0 && element) {
      element!.tabIndex = -1;
    }
  }, [tabIndex, apiRef, props.field]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === " ") {
        // imperative toggle the checkbox because Space is disable by some preventDefault
        apiRef.current.publishEvent("headerVisibilitySelectionCheckboxChange", {
          value: !isChecked,
        });
      }
    },
    [apiRef, isChecked],
  );

  const handleSelectionChange = useCallback(() => {
    forceUpdate((p) => !p);
  }, []);

  useEffect(() => {
    return apiRef.current.subscribeEvent("rowSelectionChange", handleSelectionChange);
  }, [apiRef, handleSelectionChange]);

  const label = apiRef.current.getLocaleText(isChecked ? "checkboxSelectionUnselectAllRows" : "checkboxSelectionSelectAllRows");

  const checked = rootProps.indeterminateCheckboxAction === "select" ? isChecked && !isIndeterminate : isChecked;

  return (
    <rootProps.slots.baseCheckbox
      ref={ref}
      //indeterminate={isIndeterminate}
      checked={checked}
      onChange={handleChange}
      className={classes.root}
      inputProps={{ "aria-label": label }}
      tabIndex={tabIndex}
      onKeyDown={handleKeyDown}
      disabled={!isMultipleRowSelectionEnabled(rootProps)}
      touchRippleRef={rippleRef as any /* FIXME: typing error */}
      icon={<VisibilityOffIcon fontSize="inherit" />}
      checkedIcon={<VisibilityIcon fontSize="inherit" sx={{ color: "text.primary" }} />}
      {...rootProps.slotProps?.baseCheckbox}
      {...other}
    />
  );
});

GridHeaderVisibilityCheckbox.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The column of the current header component.
   */
  colDef: PropTypes.object.isRequired,
  /**
   * The column field of the column that triggered the event
   */
  field: PropTypes.string.isRequired,
} as any;

export { GridHeaderVisibilityCheckbox };

export interface GridHeaderVisibilityProps {}

const GridHeaderVisibility = (_props: GridHeaderVisibilityProps) => {
  const theme = useTheme();
  const [allVisible, setAllVisible] = useState(true);

  // TODO: get allVisible from the grid state

  return (
    <Box className="MuiDataGrid-columnHeaderTitle visibility-grid-header" sx={{ position: "relative", alignItems: "center" }}>
      <Checkbox
        className="MuiDataGrid-cellCheckbox"
        //checked={!allVisible}
        tabIndex={-1}
        icon={<VisibilityOffIcon fontSize="inherit" />}
        checkedIcon={<VisibilityIcon fontSize="inherit" sx={{ color: theme.palette.text.primary }} />}
        sx={{ fontSize: "1rem" }}
      />
    </Box>
  );
};

export default GridHeaderVisibility;
